import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiHome, FiMapPin, FiCalendar, FiMail, FiUsers,
  FiSettings, FiMenu, FiX, FiLogOut, FiImage, FiBarChart2
} from 'react-icons/fi';
import Logo from '../components/Logo';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './AdminLayout.css';

const NAV = [
  { path: '/admin',           label: 'Dashboard',  icon: <FiBarChart2 /> },
  { path: '/admin/trips',     label: 'Trips',      icon: <FiMapPin /> },
  { path: '/admin/bookings',  label: 'Bookings',   icon: <FiCalendar /> },
  { path: '/admin/contacts',  label: 'Messages',   icon: <FiMail /> },
  { path: '/admin/users',     label: 'Users',      icon: <FiUsers /> },
  { path: '/admin/media',     label: 'Media',      icon: <FiImage /> },
  { path: '/admin/settings',  label: 'Settings',   icon: <FiSettings /> },
];

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/login');
  };

  return (
    <div className={`admin-shell ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Logo size={30} showText={sidebarOpen} textColor="white" />
          </div>
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {NAV.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
              title={!sidebarOpen ? item.label : ''}
            >
              <span className="sidebar-link-icon">{item.icon}</span>
              {sidebarOpen && <span className="sidebar-link-label">{item.label}</span>}
              {location.pathname === item.path && sidebarOpen && (
                <motion.div className="sidebar-active-bar" layoutId="adminActiveBar" />
              )}
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <Link to="/" className="sidebar-link" title="View Site" target="_blank" rel="noreferrer">
            <span className="sidebar-link-icon"><FiHome /></span>
            {sidebarOpen && <span className="sidebar-link-label">View Site</span>}
          </Link>
          <button className="sidebar-link sidebar-logout" onClick={handleLogout} title="Logout">
            <span className="sidebar-link-icon"><FiLogOut /></span>
            {sidebarOpen && <span className="sidebar-link-label">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="admin-main">
        <header className="admin-topbar">
          <div className="topbar-left">
            <button className="topbar-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <FiMenu />
            </button>
            <h1 className="topbar-title">
              {NAV.find(n => n.path === location.pathname)?.label || 'Admin'}
            </h1>
          </div>
          <div className="topbar-right">
            <div className="topbar-user">
              <div className="topbar-avatar">{user?.name?.charAt(0).toUpperCase()}</div>
              <div className="topbar-user-info">
                <span className="topbar-name">{user?.name}</span>
                <span className="topbar-role">Administrator</span>
              </div>
            </div>
          </div>
        </header>

        <div className="admin-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
